import { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Plus, MagnifyingGlass } from '@phosphor-icons/react';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const OrdersPage = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const fetchOrders = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.status_filter = statusFilter;
      }
      const response = await axios.get(`${API}/orders`, { params });
      setOrders(response.data);
    } catch (error) {
      logger.error('Error fetching orders:', error);
    }
  };

  const filteredOrders = orders.filter((order) =>
    order.order_number.toLowerCase().includes(search.toLowerCase()) ||
    order.client_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">Órdenes de Servicio</h1>
          {(user?.role === 'reception' || user?.role === 'admin') && (
            <Link to="/orders/new">
              <Button data-testid="create-order-button" className="bg-primary hover:bg-primary/90">
                <Plus size={20} className="mr-2" weight="bold" />
                Nueva Orden
              </Button>
            </Link>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Buscar por número de orden o cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-input"
              className="pl-10 border-border"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="status-filter"
            className="px-4 py-2 border border-border bg-background text-foreground font-medium"
          >
            <option value="all">Todos los Estados</option>
            <option value="pending">Pendiente</option>
            <option value="in_progress">En Proceso</option>
            <option value="completed">Completada</option>
            <option value="delivered">Entregada</option>
          </select>
        </div>

        <div className="border border-border bg-background">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-border">
                <tr className="bg-secondary">
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-[0.2em]">Número</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-[0.2em]">Cliente</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-[0.2em]">Tienda</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-[0.2em]">Fecha</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-[0.2em]">Estado</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-[0.2em]">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredOrders.length > 0 ? (
                  filteredOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-secondary/50" data-testid={`order-row-${order.id}`}>
                      <td className="px-6 py-4 font-mono font-bold">{order.order_number}</td>
                      <td className="px-6 py-4">{order.client_name}</td>
                      <td className="px-6 py-4">{order.store}</td>
                      <td className="px-6 py-4 font-mono text-sm">
                        {new Date(order.reception_date).toLocaleDateString('es-MX')}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-block px-3 py-1 text-xs font-bold uppercase tracking-wider border ${
                            order.status === 'pending'
                              ? 'border-muted bg-muted text-foreground'
                              : order.status === 'in_progress'
                              ? 'border-primary bg-primary/10 text-primary'
                              : order.status === 'completed'
                              ? 'border-foreground bg-foreground/10 text-foreground'
                              : 'border-foreground bg-foreground text-background'
                          }`}
                        >
                          {order.status === 'pending' && 'Pendiente'}
                          {order.status === 'in_progress' && 'En Proceso'}
                          {order.status === 'completed' && 'Completada'}
                          {order.status === 'delivered' && 'Entregada'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <Link to={`/orders/${order.id}`}>
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid={`view-order-${order.id}`}
                            className="hover:bg-primary hover:text-primary-foreground"
                          >
                            Ver Detalle
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-muted-foreground">
                      No se encontraron órdenes
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};
